import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 text-gray-900 font-sans antialiased">
      {/* Header with Animated Gradient */}
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white text-center py-12 shadow-xl relative overflow-hidden">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <img
            src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png"
            alt="HunterFortress Logo"
            className="mx-auto h-24 mb-6 drop-shadow-lg"
          />
          <motion.h1 
            className="text-5xl font-extrabold tracking-tight mb-3"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: "spring" }}
          >
            Terms of Service
          </motion.h1>
          <motion.p 
            className="text-xl font-light max-w-2xl mx-auto opacity-90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            The rules of engagement for our security services
          </motion.p>
        </motion.div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <motion.section 
          className="bg-white p-8 rounded-xl shadow-lg border border-blue-100 mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-blue-900 mb-6 flex items-center">
            <span className="bg-blue-100 p-2 rounded-full mr-3">⚔️</span>
            Terms of Engagement
          </h2>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            These terms govern your use of HunterFortress security services. By engaging our services, you agree to these terms in their entirety. We've kept them as straightforward as our security recommendations.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            <strong>Last Updated:</strong> August 9, 2025
          </p>
        </motion.section>

        <div className="space-y-10">
          {[
            {
              title: "Service Scope",
              icon: "🎯",
              content: (
                <div>
                  <p className="mb-4">Our security services include:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Cloud security assessments and audits</li>
                    <li>DevSecOps pipeline configuration</li>
                    <li>Infrastructure hardening recommendations</li>
                    <li>Compliance preparation guidance</li>
                  </ul>
                  <p className="mt-4">Specific deliverables will be outlined in individual Statements of Work (SOW).</p>
                </div>
              )
            },
            {
              title: "Client Responsibilities",
              icon: "🤝",
              content: (
                <div>
                  <p className="mb-4">To receive our services, you agree to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide accurate information about your infrastructure</li>
                    <li>Grant necessary access to cloud environments (read-only where possible)</li>
                    <li>Designate a technical point of contact</li>
                    <li>Implement recommended security controls in a timely manner</li>
                  </ul>
                </div>
              )
            },
            {
              title: "Security & Confidentiality",
              icon: "🤫",
              content: (
                <div>
                  <p className="mb-4">We treat your systems and data with utmost security:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>All engagements begin with a mutual NDA</li>
                    <li>Findings are shared only through encrypted channels</li>
                    <li>We never retain administrative credentials after engagements</li>
                    <li>All consultants undergo rigorous background checks</li>
                  </ul>
                </div>
              )
            },
            {
              title: "Intellectual Property",
              icon: "🧠",
              content: (
                <div>
                  <p className="mb-4">Ownership rights:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You retain all rights to your systems and data</li>
                    <li>We retain rights to our methodologies, tools, and templates</li>
                    <li>Deliverables are licensed for your internal use only</li>
                  </ul>
                </div>
              )
            },
            {
              title: "Limitations",
              icon: "⚠️",
              content: (
                <div>
                  <p className="mb-4">Important disclaimers:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>We cannot guarantee 100% security - no one can</li>
                    <li>Our liability is limited to fees paid for services</li>
                    <li>We're not responsible for third-party systems/components</li>
                    <li>Penetration testing requires separate authorization</li>
                  </ul>
                </div>
              )
            },
            {
              title: "Payment & Termination",
              icon: "💳",
              content: (
                <div>
                  <p className="mb-4">Financial terms:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>50% deposit required before work begins</li>
                    <li>Balance due upon delivery of final report</li>
                    <li>Enterprise retainers billed monthly in advance</li>
                    <li>Either party may terminate with 14 days written notice</li>
                  </ul>
                </div>
              )
            }
          ].map((section, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-xl shadow-md border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-blue-800 mb-4 flex items-center">
                <span className="bg-blue-100 p-2 rounded-full mr-3">{section.icon}</span>
                {section.title}
              </h3>
              <div className="text-gray-700 leading-relaxed">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-4 md:mb-0">
            <img src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png" alt="Logo" className="h-10 mr-3" />
            <span className="text-xl font-bold">HunterFortress</span>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
			<Link to="/" className="hover:text-blue-300 transition font-semibold">Home</Link>
            <Link to="/contact" className="hover:text-blue-300 transition font-semibold">Contact</Link>
            <Link to="/storepal" className="hover:text-blue-300 transition font-semibold">StorePal</Link>
            <Link to="/terms" className="hover:text-blue-300 transition font-semibold">Terms</Link>
			<Link to="/privacy" className="hover:text-blue-300 transition">Privacy</Link>
			<Link to="/blog" className="hover:text-blue-300 transition">Blog</Link>
          </div>
        </div>
        <div className="text-center mt-6 pt-6 border-t border-blue-700">
          <p>&copy; 2025 HunterFortress. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;